import { useState, useEffect, useRef, useCallback } from "react";
import {
  FetchMoreQueryOptions,
  FetchMoreOptions,
  type FetchResult,
} from "@apollo/client";
import {
  ReviewDiscoveryListQuery,
  ReviewDiscoveryListQueryVariables,
} from "../../../__generated__/graphql";

type FetchMoreFn = (
  options: FetchMoreQueryOptions<
    ReviewDiscoveryListQuery,
    ReviewDiscoveryListQueryVariables
  > &
    FetchMoreOptions<ReviewDiscoveryListQuery, ReviewDiscoveryListQuery>,
) => Promise<unknown>;

export type { FetchMoreFn };

interface UseInfiniteScrollReviewsOptions {
  limit?: number;
  hasLastLo: boolean;
  getLocationFromStorage: () => { lat: number; lng: number };
  /** ref that will be set with fetchMore from useExploreQuery (avoids circular dependency) */
  fetchMoreRef: React.MutableRefObject<FetchMoreFn | null>;
  selectedFilter: "all" | "nearby";
  storageKey?: string;
}

export const useInfiniteScrollReviews = ({
  limit = 10,
  hasLastLo,
  getLocationFromStorage,
  fetchMoreRef,
  selectedFilter,
  storageKey = "explore",
}: UseInfiniteScrollReviewsOptions) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // sessionStorage에서 복원
  const restoreFromStorage = useCallback(() => {
    try {
      const savedReviews = sessionStorage.getItem(`${storageKey}:allReviews`);
      const savedOffset = sessionStorage.getItem(`${storageKey}:offset`);
      const savedHasMore = sessionStorage.getItem(`${storageKey}:hasMore`);
      const savedFilter = sessionStorage.getItem(
        `${storageKey}:selected_filter`,
      );

      // 필터가 변경되었으면 저장된 데이터 무효화
      if (savedFilter && savedFilter !== selectedFilter) {
        sessionStorage.removeItem(`${storageKey}:allReviews`);
        sessionStorage.removeItem(`${storageKey}:offset`);
        sessionStorage.removeItem(`${storageKey}:hasMore`);
        return null;
      }

      if (savedReviews && savedOffset && savedHasMore) {
        const parsedReviews = JSON.parse(savedReviews);
        return {
          reviews: parsedReviews,
          offset: parseInt(savedOffset, 10),
          hasMore: savedHasMore === "true",
        };
      }
    } catch (error) {
      console.error("Failed to restore from storage:", error);
    }
    return null;
  }, [storageKey, selectedFilter]);

  const restoredData = restoreFromStorage();
  const [offset, setOffset] = useState(restoredData?.offset || 0);
  const [allReviews, setAllReviews] = useState<
    ReviewDiscoveryListQuery["reviewDiscoveryList"]
  >(restoredData?.reviews || []);
  const [hasMore, setHasMore] = useState(restoredData?.hasMore ?? true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initLoading, setInitLoading] = useState(
    !restoredData || !restoredData.reviews || restoredData.reviews.length === 0,
  );

  // allReviews 변경 시 sessionStorage에 저장
  useEffect(() => {
    if (allReviews && allReviews.length > 0) {
      try {
        sessionStorage.setItem(
          `${storageKey}:allReviews`,
          JSON.stringify(allReviews),
        );
        sessionStorage.setItem(`${storageKey}:offset`, offset.toString());
        sessionStorage.setItem(`${storageKey}:hasMore`, hasMore.toString());
        sessionStorage.setItem(`${storageKey}:selected_filter`, selectedFilter);
      } catch (error) {
        console.error("Failed to save to storage:", error);
      }
    }
  }, [allReviews, offset, hasMore, selectedFilter, storageKey]);

  // 복원된 데이터가 있으면 즉시 로딩 완료 처리
  useEffect(() => {
    if (
      restoredData &&
      restoredData.reviews &&
      restoredData.reviews.length > 0
    ) {
      setInitLoading(false);
    } else if (allReviews && allReviews.length > 0) {
      // 데이터가 로드되면 로딩 상태 해제
      setInitLoading(false);
    }
  }, [restoredData, allReviews]);

  // 필터가 변경되면 저장된 데이터 무효화 및 상태 초기화
  useEffect(() => {
    const savedFilter = sessionStorage.getItem(`${storageKey}:selected_filter`);
    if (savedFilter && savedFilter !== selectedFilter) {
      // 필터가 변경되었으므로 상태 초기화
      setOffset(0);
      setAllReviews([]);
      setHasMore(true);
      setInitLoading(true);
    }
  }, [selectedFilter, storageKey]);

  // 더 많은 리뷰 불러오기 함수
  const handleLoadMore = useCallback(async () => {
    const fetchMore = fetchMoreRef.current;
    if (isLoadingMore || !hasMore || !fetchMore) return;

    setIsLoadingMore(true);

    try {
      const options = {
        variables: {
          isNearby: selectedFilter === "nearby",
          lat: hasLastLo ? getLocationFromStorage().lat : null,
          lng: hasLastLo ? getLocationFromStorage().lng : null,
          offset: offset,
          limit: limit,
        },
      } as FetchMoreQueryOptions<
        ReviewDiscoveryListQuery,
        ReviewDiscoveryListQueryVariables
      > &
        FetchMoreOptions<ReviewDiscoveryListQuery>;
      const result = (await fetchMore(
        options,
      )) as FetchResult<ReviewDiscoveryListQuery>;

      if (result.data?.reviewDiscoveryList) {
        const newReviews = result.data.reviewDiscoveryList;

        // 중복 제거하여 추가
        setAllReviews((prev) => {
          const existingIds = new Set(prev.map((review) => review?.id));
          const uniqueNewReviews = newReviews.filter(
            (review: ReviewDiscoveryListQuery["reviewDiscoveryList"][number]) =>
              review && !existingIds.has(review.id),
          );
          return [...prev, ...uniqueNewReviews];
        });

        setOffset((prev) => prev + newReviews.length);
        setHasMore(newReviews.length === limit);
        sessionStorage.setItem(
          `${storageKey}:offset`,
          (offset + newReviews.length).toString(),
        );
      }
    } catch (error) {
      console.error("Failed to load more reviews:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    selectedFilter,
    hasLastLo,
    getLocationFromStorage,
    offset,
    limit,
    storageKey,
    fetchMoreRef,
  ]);

  // Intersection Observer로 무한 스크롤 구현
  useEffect(() => {
    if (!hasMore || isLoadingMore || !allReviews || allReviews.length === 0)
      return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: rootRef.current,
        rootMargin: "300px",
      },
    );

    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, handleLoadMore, allReviews.length]);

  return {
    rootRef,
    loadMoreRef,
    allReviews,
    setAllReviews,
    offset,
    setOffset,
    hasMore,
    setHasMore,
    isLoadingMore,
    initLoading,
    setInitLoading,
    restoredData,
  };
};
